import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';

const UserDetailModal = ({ user, onClose, onUpdate }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [addGameModal, setAddGameModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState('');

  useEffect(() => {
    fetchUserDetails();
  }, [user.id]);

  useEffect(() => {
    if (userDetails) {
      setEditForm({
        first_name: userDetails.user.first_name || '',
        last_name: userDetails.user.last_name || '',
        email: userDetails.user.email || '',
        phone_number: userDetails.user.phone_number || '',
        region: userDetails.user.region || '',
        flight_school: userDetails.user.flight_school || '',
        is_active: userDetails.user.is_active,
        is_staff: userDetails.user.is_staff,
      });
    }
  }, [userDetails]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/auth/admin/users/${user.id}/`);
      setUserDetails(response);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      await apiClient.patch(`/api/auth/admin/users/${user.id}/`, editForm);
      setEditMode(false);
      fetchUserDetails();
      onUpdate();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleAddGameAccess = async () => {
    if (!selectedGame) return;
    try {
      const activeSub = userDetails.subscriptions?.find(s => s.is_active);
      if (!activeSub) {
        alert('User must have an active subscription to add game access');
        return;
      }
      await apiClient.post(`/api/auth/admin/subscriptions/${activeSub.id}/game-access/`, {
        game_type: selectedGame
      });
      setAddGameModal(false);
      setSelectedGame('');
      fetchUserDetails();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to add game access');
    }
  };

  const handleRemoveGameAccess = async (gameAccessId) => {
    if (!window.confirm('Remove this game access?')) return;
    try {
      await apiClient.delete(`/api/auth/admin/game-access/${gameAccessId}/`);
      fetchUserDetails();
    } catch (error) {
      alert('Failed to remove game access');
    }
  };

  const handleExtendSubscription = async (subId) => {
    const days = prompt('Enter number of days to extend:');
    if (!days) return;
    try {
      await apiClient.post(`/api/auth/admin/subscriptions/${subId}/extend/`, { days: parseInt(days) });
      fetchUserDetails();
    } catch (error) {
      alert('Failed to extend subscription');
    }
  };

  const handleRevokeSubscription = async (subId) => {
    if (!window.confirm('Revoke this subscription?')) return;
    try {
      await apiClient.delete(`/api/auth/admin/subscriptions/${subId}/revoke/`);
      fetchUserDetails();
    } catch (error) {
      alert('Failed to revoke subscription');
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      `⚠️ WARNING: This will permanently delete the account for ${userDetails.user.email} and ALL associated data.\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Are you sure you want to proceed?`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await apiClient.delete(`/api/auth/admin/users/${user.id}/`);
      alert('User account deleted successfully');
      onClose();
      onUpdate();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete user account');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!userDetails) return null;

  const tabs = [
    { id: 'account', label: 'Account Info' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'games', label: 'Game Access' },
    { id: 'history', label: 'Purchase History' },
    { id: 'coupons', label: 'Coupons' },
    { id: 'exams', label: 'Exams' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {userDetails.user.full_name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{userDetails.user.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-4 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Account Info Tab */}
          {activeTab === 'account' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Information</h3>
                {!editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <PencilIcon className="h-4 w-4" /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateUser}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-3 py-1.5 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">First Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userDetails.user.first_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Last Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userDetails.user.last_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                  {editMode ? (
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userDetails.user.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Phone</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userDetails.user.phone_number || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Region</label>
                  {editMode ? (
                    <select
                      value={editForm.region}
                      onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="easa">EASA</option>
                      <option value="faa">FAA</option>
                      <option value="gcaa">GCAA</option>
                    </select>
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{userDetails.user.region || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Flight School</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editForm.flight_school}
                      onChange={(e) => setEditForm({ ...editForm, flight_school: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userDetails.user.flight_school || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Joined</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {new Date(userDetails.user.date_joined).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Last Login</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {userDetails.user.last_login ? new Date(userDetails.user.last_login).toLocaleString() : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Account Status</label>
                  {editMode ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm">Active</span>
                    </label>
                  ) : (
                    <span className={`text-sm font-medium ${userDetails.user.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {userDetails.user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Staff Status</label>
                  {editMode ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editForm.is_staff}
                        onChange={(e) => setEditForm({ ...editForm, is_staff: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm">Staff</span>
                    </label>
                  ) : (
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {userDetails.user.is_staff ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
              </div>

              {/* Danger Zone - Delete Account */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-red-900 dark:text-red-400 mb-2">Danger Zone</h4>
                  <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                    Permanently delete this user account and all associated data. This action cannot be undone.
                  </p>
                  <button
                    onClick={handleDeleteAccount}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Subscriptions ({userDetails.subscriptions?.length || 0})
              </h3>
              {userDetails.subscriptions?.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No subscriptions</p>
              ) : (
                userDetails.subscriptions.map(sub => (
                  <div key={sub.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{sub.course_name}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            sub.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {sub.is_active ? 'Active' : 'Expired'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <div>Tier: {sub.tier_name}</div>
                          <div>Amount: {sub.amount_paid} {sub.currency}</div>
                          <div>Purchased: {new Date(sub.purchased_at).toLocaleDateString()}</div>
                          <div>Expires: {sub.access_expires_at ? new Date(sub.access_expires_at).toLocaleDateString() : 'Never'}</div>
                          <div>Days Left: {sub.days_remaining}</div>
                          <div>Method: {sub.payment_method}</div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleExtendSubscription(sub.id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                          title="Extend"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        {sub.is_active && (
                          <button
                            onClick={() => handleRevokeSubscription(sub.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title="Revoke"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Game Access Tab */}
          {activeTab === 'games' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Game Access ({userDetails.game_access?.length || 0})
                </h3>
                <button
                  onClick={() => setAddGameModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <PlusIcon className="h-4 w-4" /> Add Game
                </button>
              </div>
              {userDetails.game_access?.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No game access</p>
              ) : (
                <div className="space-y-2">
                  {userDetails.game_access.map(ga => (
                    <div key={ga.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{ga.game_type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          ga.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {ga.is_active ? 'Active' : 'Expired'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Expires: {ga.access_expires_at ? new Date(ga.access_expires_at).toLocaleDateString() : 'Never'}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveGameAccess(ga.id)}
                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Purchase History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Purchase History ({userDetails.payments?.length || 0})
              </h3>
              {userDetails.payments?.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No purchases</p>
              ) : (
                <div className="space-y-2">
                  {userDetails.payments.map(payment => (
                    <div key={payment.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {payment.amount} JOD
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {payment.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {payment.payment_method} • {new Date(payment.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Coupons Tab */}
          {activeTab === 'coupons' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Coupon Usage</h3>
              {userDetails.purchases?.filter(p => p.coupon_code).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No coupons used</p>
              ) : (
                <div className="space-y-3">
                  {userDetails.purchases?.filter(p => p.coupon_code).map((purchase) => (
                    <div key={purchase.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
                              {purchase.coupon_code}
                            </span>
                            {purchase.discount_amount > 0 && (
                              <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                                -{purchase.discount_amount} JOD
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{purchase.course_name}</span> - {purchase.tier_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Used on {new Date(purchase.purchased_at).toLocaleDateString()} • 
                            Final Price: {purchase.amount_paid} JOD
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Exams Tab */}
          {activeTab === 'exams' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Exam Attempts</h3>
              {!userDetails.exam_attempts || userDetails.exam_attempts.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No exam attempts</p>
              ) : (
                <div className="space-y-3">
                  {userDetails.exam_attempts?.map((exam) => (
                    <div key={exam.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {exam.exam_name || exam.subject_name}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              exam.status === 'completed' 
                                ? exam.passed 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {exam.status === 'completed' ? (exam.passed ? 'Passed' : 'Failed') : exam.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <div>Score: <span className="font-medium text-gray-900 dark:text-white">{exam.score}%</span></div>
                            <div>Duration: <span className="font-medium">{exam.duration_minutes || 0} min</span></div>
                            <div>Questions: <span className="font-medium">{exam.total_questions || 0}</span></div>
                            <div>Correct: <span className="font-medium text-green-600 dark:text-green-400">{exam.correct_answers || 0}</span></div>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            {new Date(exam.started_at).toLocaleString()}
                            {exam.completed_at && ` - ${new Date(exam.completed_at).toLocaleString()}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Game Access Modal */}
      {addGameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Game Access</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Game
              </label>
              <select
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              >
                <option value="">Choose a game...</option>
                {['SKT', 'MIC', 'MEK', 'RMS', 'ROT', 'OWT', 'PPT'].map(game => (
                  <option key={game} value={game}>{game}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setAddGameModal(false); setSelectedGame(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddGameAccess}
                disabled={!selectedGame}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Add Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailModal;
